/**
 * Shared full-page states for order routes (loading / not found / load error).
 *
 * Used by both `OrderScopeGuard` and pages that act as their own scope check
 * (e.g. `OrderInProgress`, whose order fetch is org-scoped server-side), so a
 * user moving between order routes sees one consistent loader and one
 * consistent not-found / error screen.
 */

export const ORDER_LOADING_MESSAGE = 'Loading order…'

export function OrderLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-variant-gray dark:bg-gray-950">
      <div className="w-full max-w-md p-8">
        <div className="rounded-base border border-border-base bg-white p-8 shadow-card dark:border-gray-700 dark:bg-gray-900">
          <div className="text-center" role="status" aria-live="polite">
            <div
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] dark:border-brand-500"
              aria-hidden="true"
            />
            <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">{ORDER_LOADING_MESSAGE}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function OrderNotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-variant-gray px-4 dark:bg-gray-950">
      <div
        role="alert"
        className="w-full max-w-lg rounded-base border border-border-base bg-white p-8 text-center shadow-card dark:border-gray-700 dark:bg-gray-900"
      >
        <h1 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">Order not found</h1>
        <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">
          This order does not exist or you do not have access to view it.
        </p>
      </div>
    </div>
  )
}

type OrderLoadErrorProps = {
  title?: string
  message: string
}

export function OrderLoadError({ title = 'Unable to load order', message }: OrderLoadErrorProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-variant-gray px-4 dark:bg-gray-950">
      <div
        role="alert"
        className="w-full max-w-lg rounded-base border border-border-base bg-white p-8 text-center shadow-card dark:border-gray-700 dark:bg-gray-900"
      >
        <h1 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">{title}</h1>
        <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">{message}</p>
      </div>
    </div>
  )
}
